import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiUpload, FiImage, FiSave, FiArrowLeft } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'
import currencies from '../../utils/currencies'
import { formatCurrencyInput, unformatCurrencyInput } from '../../utils/formatters'

export default function AddItem() {
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    quantity: '1',
    currency: '',
    status: 'pending',
    category: '',
    tags: '',
    imageUrl: '',
    customerName: ''
  })
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState('')
  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === 'price') {
      // Keep raw numeric value while typing (remove commas and non-numeric except dot)
      const cleaned = unformatCurrencyInput(value).replace(/[^\d.]/g, '')
      setFormData(prev => ({ ...prev, price: cleaned }))
    } else if (name === 'quantity') {
      // Only allow integer quantity values
      const cleaned = value.replace(/[^\d]/g, '')
      setFormData(prev => ({ ...prev, quantity: cleaned }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handlePriceFocus = () => {
    setFormData(prev => ({ ...prev, price: unformatCurrencyInput(prev.price) }))
  }

  const handlePriceBlur = () => {
    setFormData(prev => ({ ...prev, price: formatCurrencyInput(prev.price) }))
  }
  

  const handleImageFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image size should be less than 10MB')
        return
      }
      
      setImageFile(file)
      
      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreview(e.target.result)
        setFormData(prev => ({ ...prev, imageUrl: '' }))
      }
      reader.readAsDataURL(file)
    }
  }

  const handleImageUrlChange = (e) => {
    const url = e.target.value
    setFormData(prev => ({ ...prev, imageUrl: url }))
    if (url) {
      setImagePreview(url)
      setImageFile(null)
    } else {
      setImagePreview('')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Clean price (remove thousands separators) for validation and submission
    const cleanedPriceStr = formData.price?.toString().replace(/,/g, '') || ''
    const priceValue = parseFloat(cleanedPriceStr)

    if (!formData.name || !cleanedPriceStr || !formData.customerName) {
      toast.error('Please fill in all required fields')
      return
    }

    if (isNaN(priceValue) || priceValue <= 0) {
      toast.error('Price must be greater than 0')
      return
    }

    const qtyValue = parseInt(formData.quantity || '1', 10)
    if (isNaN(qtyValue) || qtyValue < 1) {
      toast.error('Quantity must be at least 1')
      return
    }

    setLoading(true)

    try {
      const submitData = new FormData()
      submitData.append('name', formData.name)
      submitData.append('customerName', formData.customerName)
      submitData.append('description', formData.description)
      submitData.append('price', cleanedPriceStr)
      submitData.append('currency', formData.currency)
      submitData.append('status', formData.status)
      submitData.append('category', formData.category)
      submitData.append('tags', formData.tags)
      submitData.append('imageUrl', formData.imageUrl)
      submitData.append('quantity', qtyValue)
      
      if (imageFile) {
        submitData.append('image', imageFile)
      }

      await axios.post('/items', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      toast.success('Item added successfully!')
      navigate('/items')
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error adding item')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get('/auth/profile')
        const u = res.data.user
        const defaultCurrency = u.preferredCurrency || u.adminConfig?.defaultCurrency || 'NGN'
        setFormData(prev => ({ ...prev, currency: defaultCurrency }))
      } catch (err) {
        // ignore
      }
    }
    fetchProfile()
  }, [])

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'paid', label: 'Paid' },
    { value: 'unpaid', label: 'Unpaid' }
  ]

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/items')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <FiArrowLeft className="w-4 h-4 mr-2" />
          Back to Items
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Add New Item</h1>
        <p className="text-gray-600">Create a new marketplace item with invoice generation</p>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Item Name *
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter item name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Name *
            </label>
            <input
              type="text"
              name="customerName"
              required
              value={formData.customerName}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter customer name for invoice"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              className="input-field"
              placeholder="Describe your item..."
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price *
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="price"
                  required
                  value={formData.price}
                  onChange={handleChange}
                  onFocus={handlePriceFocus}
                  onBlur={handlePriceBlur}
                  className="flex-1 input-field"
                  placeholder="0.00"
                />
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="input-field w-24 sm:w-28"
                >
                  {([ { code: 'all', name: 'Select Currency' } ].concat(currencies)).map(c => (
                    <option key={c.code} value={c.code}>
                      {c.code}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Qty *
              </label>
              <input
                type="number"
                name="quantity"
                min="1"
                required
                value={formData.quantity}
                onChange={handleChange}
                className="input-field"
                placeholder="1"
              />
            </div>

            <div className="sm:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="input-field"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category
            </label>
            <input
              type="text"
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="input-field"
              placeholder="e.g., Electronics, Clothing, Services"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              className="input-field"
              placeholder="Enter tags separated by commas"
            />
            <p className="text-xs text-gray-500 mt-1">
              Separate multiple tags with commas (e.g., new, premium, limited)
            </p>
          </div>
        </div>

        {/* Image Upload */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Item Image</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload from Device
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 text-center hover:border-primary-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageFileChange}
                  className="hidden"
                  id="image-upload"
                />
                <label htmlFor="image-upload" className="cursor-pointer block">
                  <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    Click to upload
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    PNG, JPG, GIF up to 10MB
                  </p>
                </label>
              </div>
            </div>

            {/* URL Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Or Enter Image URL
              </label>
              <div className="space-y-2">
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={handleImageUrlChange}
                  className="input-field"
                  placeholder="https://example.com/image.jpg"
                />
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <FiImage className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    Enter a valid image URL above
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Image Preview */}
          {imagePreview && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preview
              </label>
              <div className="border rounded-lg p-4">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-w-full h-48 object-cover rounded-lg mx-auto"
                />
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex gap-3 pt-6 border-t border-gray-200 flex-col sm:flex-row">
          <button
            type="button"
            onClick={() => navigate('/items')}
            className="flex-1 btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 btn-primary flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                <span>Adding...</span>
              </>
            ) : (
              <>
                <FiSave className="w-4 h-4" />
                <span>Add Item</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}